/**
 * Today Decision Engine — 唯一 Daily Decision Source
 *
 * 输入：Strategy + CharacterMemory（由 OperationalSnapshot 经 getCharacterMemory 派生）
 * 输出：TodayDecision
 *
 * 流程：
 * CharacterMemory → CharacterOperationBrain → MarketingBrain → PlatformBrain → TodayDecision
 */
import { buildCharacterOperation } from './characterOperationBrain';
import { constraintsFromCharacterMemory, getCharacterMemory } from './characterMemory';
import { buildManagerExplanation } from './explainBrain';
import { buildMarketingDecision } from './marketingBrain';
import type { MarketingDecision } from './marketingTypes';
import { buildPlatformDecision } from './platformBrain';
import type { PlatformDecision } from './platformTypes';
import type { CharacterOperation } from './operationTypes';
import type {
  DecisionTone,
  ExecutionJobType,
  OperationalSnapshot,
  PhotoIntent,
  StrategyContext,
  TodayDecision,
  TodayDecisionMaterialSlot,
} from './types';

function slot(
  enabled: boolean,
  count: number,
  reason: string,
  photoIntent?: PhotoIntent,
): TodayDecisionMaterialSlot {
  return { enabled, count, reason, photoIntent };
}

function disabledSlot(reason: string): TodayDecisionMaterialSlot {
  return slot(false, 0, reason);
}

/** 依 MarketingDecision.marketing_mode 映射语气（不自行判断是否推广 / 平台） */
function mapMarketingToTone(marketing: MarketingDecision): DecisionTone {
  switch (marketing.marketing_mode) {
    case 'rest':
      return 'rest';
    case 'trust':
      return 'life';
    case 'interaction':
      return 'outreach';
    case 'promotion':
    case 'campaign':
      return 'commercial';
    case 'knowledge':
      return 'outreach';
    default:
      return 'life';
  }
}

function pickPhotoIntent(tone: DecisionTone, blockSelfie: boolean): PhotoIntent {
  if (tone === 'commercial') return 'commercial_pose';
  if (tone === 'outreach' && !blockSelfie) return 'selfie';
  if (tone === 'rest') return 'life';
  return 'friend_candid';
}

/**
 * 依 marketing_mode 映射素材开关。
 * 平台选择只读 PlatformDecision，不在此做平台 if/switch。
 */
function buildMaterialsFromMarketing(
  marketing: MarketingDecision,
  platform: PlatformDecision,
  blockSelfie: boolean,
): Record<ExecutionJobType, TodayDecisionMaterialSlot> {
  const tone = mapMarketingToTone(marketing);
  const reasonPrefix = [
    ...marketing.promotion_reason,
    ...platform.platform_reason,
  ].join('；');

  // 平台 cooldown：不生成素材（读 visibility_mode，不判断具体平台 id）
  if (platform.visibility_mode === 'cooldown') {
    return {
      photo: disabledSlot(`${reasonPrefix}；平台 cooldown，不生成照片`),
      voice: disabledSlot(`${reasonPrefix}；平台 cooldown，不生成配音`),
      talking_video: disabledSlot(`${reasonPrefix}；平台 cooldown，不生成口播`),
      short_video: disabledSlot(`${reasonPrefix}；平台 cooldown，不生成短片`),
    };
  }

  switch (marketing.marketing_mode) {
    case 'rest':
      return {
        photo: disabledSlot(`${reasonPrefix}；休息日不生成照片`),
        voice: disabledSlot(`${reasonPrefix}；休息日不生成配音`),
        talking_video: disabledSlot(`${reasonPrefix}；休息日不生成口播`),
        short_video: disabledSlot(`${reasonPrefix}；休息日不生成短片`),
      };
    case 'trust': {
      const photoIntent = pickPhotoIntent('life', blockSelfie);
      return {
        photo: slot(true, 3, `${reasonPrefix}；信任日生活感照片`, photoIntent),
        voice: disabledSlot(`${reasonPrefix}；信任日不生成配音`),
        talking_video: disabledSlot(`${reasonPrefix}；信任日不生成口播`),
        short_video: disabledSlot(`${reasonPrefix}；信任日不生成短片`),
      };
    }
    case 'interaction':
      return {
        photo: disabledSlot(`${reasonPrefix}；互动日不生成照片`),
        voice: disabledSlot(`${reasonPrefix}；互动日不生成配音`),
        talking_video: disabledSlot(`${reasonPrefix}；互动日不生成口播`),
        short_video: disabledSlot(`${reasonPrefix}；互动日不生成短片`),
      };
    case 'knowledge':
      return {
        photo: disabledSlot(`${reasonPrefix}；知识分享不生成照片`),
        voice: slot(true, 1, `${reasonPrefix}；知识分享需要配音`),
        talking_video: disabledSlot(`${reasonPrefix}；知识分享不生成口播`),
        short_video: disabledSlot(`${reasonPrefix}；知识分享不生成短片`),
      };
    case 'promotion':
    case 'campaign': {
      if (!marketing.promotion_allowed) {
        const photoIntent = pickPhotoIntent('life', blockSelfie);
        return {
          photo: slot(true, 3, `${reasonPrefix}；推广未获准，改信任日照片`, photoIntent),
          voice: disabledSlot(`${reasonPrefix}；推广未获准，不生成配音`),
          talking_video: disabledSlot(`${reasonPrefix}；推广未获准，不生成口播`),
          short_video: disabledSlot(`${reasonPrefix}；推广未获准，不生成短片`),
        };
      }
      return {
        photo: slot(true, 2, `${reasonPrefix}；轻量推广需要照片`, pickPhotoIntent('commercial', blockSelfie)),
        voice: slot(true, 1, `${reasonPrefix}；轻量推广需要配音`),
        talking_video: disabledSlot(`${reasonPrefix}；轻量推广不生成口播`),
        short_video: disabledSlot(`${reasonPrefix}；轻量推广不生成短片`),
      };
    }
    default:
      return {
        photo: slot(true, 3, `${reasonPrefix}；默认信任日照片`, pickPhotoIntent(tone, blockSelfie)),
        voice: disabledSlot(`${reasonPrefix}；默认不生成配音`),
        talking_video: disabledSlot(`${reasonPrefix}；默认不生成口播`),
        short_video: disabledSlot(`${reasonPrefix}；默认不生成短片`),
      };
  }
}

function applyMemoryToMaterials(
  materials: Record<ExecutionJobType, TodayDecisionMaterialSlot>,
  memoryConstraints: ReturnType<typeof constraintsFromCharacterMemory>,
): Record<ExecutionJobType, TodayDecisionMaterialSlot> {
  const next = { ...materials };

  if (memoryConstraints.blockSelfie && next.photo.photoIntent === 'selfie') {
    next.photo = {
      ...next.photo,
      photoIntent: 'friend_candid',
      reason: `${next.photo.reason}；记忆规则：避免自拍`,
    };
  }

  if (memoryConstraints.blockTalkingVideo) {
    next.talking_video = disabledSlot('记忆规则：今天暂缓口播');
    if (next.voice.enabled) {
      next.voice = disabledSlot('记忆规则：暂缓配音');
    }
  }

  if (memoryConstraints.forceRestDay) {
    next.talking_video = disabledSlot('记忆规则：今天休息，不生成口播');
    next.voice = disabledSlot('记忆规则：今天休息，不生成配音');
  }

  return next;
}

function buildTextSlots(marketing: MarketingDecision, restDay: boolean) {
  const dmEnabled = !restDay && marketing.marketing_mode !== 'rest';
  const momentsEnabled = marketing.marketing_mode === 'trust' || marketing.marketing_mode === 'rest';
  const leadHookEnabled = marketing.promotion_allowed || marketing.marketing_mode === 'interaction';

  return {
    dm_opener: {
      enabled: dmEnabled,
      count: dmEnabled ? 3 : 0,
      reason: dmEnabled ? '主动开发需要私信破冰' : '今天私信话术非优先',
    },
    moments: {
      enabled: momentsEnabled,
      count: momentsEnabled ? 2 : 0,
      reason: momentsEnabled ? '生活感朋友圈铺垫' : '今天不发朋友圈文案',
    },
    lead_hook: {
      enabled: leadHookEnabled,
      count: leadHookEnabled ? 1 : 0,
      reason: leadHookEnabled ? '营销决策允许引流钩子' : '今天暂不生成引流钩子',
    },
  };
}

export function buildTodayDecisionFromPlatform(
  operation: CharacterOperation,
  marketing: MarketingDecision,
  platform: PlatformDecision,
  input: {
    characterId: number;
    decisionDate: string;
    strategy: StrategyContext;
    memoryConstraints: ReturnType<typeof constraintsFromCharacterMemory>;
  },
): TodayDecision {
  const tone = mapMarketingToTone(marketing);
  const restDay =
    marketing.marketing_mode === 'rest' || platform.visibility_mode === 'cooldown';

  let materials = buildMaterialsFromMarketing(
    marketing,
    platform,
    input.memoryConstraints.blockSelfie,
  );
  materials = applyMemoryToMaterials(materials, input.memoryConstraints);

  const strategyNotes: string[] = [];
  if (input.strategy.weeklyGoal) {
    strategyNotes.push(`本周目标：${input.strategy.weeklyGoal}`);
  }
  if (input.strategy.doNotThisWeek.length > 0) {
    strategyNotes.push(`本周避免：${input.strategy.doNotThisWeek.join('、')}`);
  }

  // 只读 PlatformDecision 字段，不做平台 id 分支
  strategyNotes.push(`主平台：${platform.primary_platform ?? 'none'}`);
  strategyNotes.push(`发布风格：${platform.posting_style}`);
  strategyNotes.push(`商业表达：${platform.commercial_style}`);
  strategyNotes.push(`可见度：${platform.visibility_mode}`);

  const blockers = input.strategy.doNotThisWeek.map((s) => s.toLowerCase());
  if (blockers.some((b) => b.includes('口播') || b.includes('视频'))) {
    materials.talking_video = disabledSlot('策略要求：本周避免口播');
    materials.short_video = disabledSlot('策略要求：本周避免视频');
  }
  if (blockers.some((b) => b.includes('自拍'))) {
    materials.photo = {
      ...materials.photo,
      photoIntent: 'friend_candid',
      reason: `${materials.photo.reason}；策略要求：本周避免自拍`,
    };
  }

  const decision: TodayDecision = {
    characterId: input.characterId,
    decisionDate: input.decisionDate,
    sourceBoardId: input.strategy.boardId,
    dayIndex: input.strategy.dayIndex,
    tone,
    restDay,
    materials,
    text: buildTextSlots(marketing, restDay),
    memoryNotes: input.memoryConstraints.notes,
    strategyNotes,
    reason: [
      ...operation.today_reason,
      ...marketing.promotion_reason,
      ...platform.platform_reason,
    ],
    marketing,
    platform,
    explanation: {
      today_summary: '',
      today_goal: '',
      today_reason: '',
      today_usage: '',
      today_do: [],
      today_dont: [],
      today_expected_result: [],
      today_next_step: '',
      today_warning: '',
      today_value: '',
    },
  };

  // Explain Layer：只翻译，不改写 Decision
  decision.explanation = buildManagerExplanation({
    operation,
    marketing,
    platform,
    decision,
  });

  return decision;
}

/**
 * 生成今日决策 — 全产品唯一 Daily Decision Source
 */
export function buildTodayDecision(input: {
  characterId: number;
  decisionDate: string;
  strategy: StrategyContext;
  memory: OperationalSnapshot;
}): TodayDecision {
  const characterMemory = getCharacterMemory({
    snapshot: input.memory,
    todayIndex: input.strategy.dayIndex,
    asOfDate: input.decisionDate,
  });
  const memoryConstraints = constraintsFromCharacterMemory(characterMemory);

  const operation = buildCharacterOperation({
    strategy: input.strategy,
    memory: characterMemory,
    today_index: input.strategy.dayIndex,
  });

  const marketing = buildMarketingDecision({
    strategy: input.strategy,
    memory: characterMemory,
    operation,
    today_index: input.strategy.dayIndex,
  });

  const platform = buildPlatformDecision({
    strategy: input.strategy,
    marketing,
    operation,
    today_index: input.strategy.dayIndex,
  });

  return buildTodayDecisionFromPlatform(operation, marketing, platform, {
    characterId: input.characterId,
    decisionDate: input.decisionDate,
    strategy: input.strategy,
    memoryConstraints,
  });
}

/** @deprecated 保留别名，内部已走 MarketingBrain + PlatformBrain */
export function buildTodayDecisionFromMarketing(
  operation: CharacterOperation,
  marketing: MarketingDecision,
  input: {
    characterId: number;
    decisionDate: string;
    strategy: StrategyContext;
    memoryConstraints: ReturnType<typeof constraintsFromCharacterMemory>;
  },
): TodayDecision {
  const platform = buildPlatformDecision({
    strategy: input.strategy,
    marketing,
    operation,
    today_index: input.strategy.dayIndex,
  });
  return buildTodayDecisionFromPlatform(operation, marketing, platform, input);
}

/** @deprecated 保留别名 */
export function buildTodayDecisionFromOperation(
  operation: CharacterOperation,
  input: {
    characterId: number;
    decisionDate: string;
    strategy: StrategyContext;
    memoryConstraints: ReturnType<typeof constraintsFromCharacterMemory>;
    memory?: import('./memoryTypes').CharacterMemory;
  },
): TodayDecision {
  const memory =
    input.memory ??
    ({
      characterId: input.characterId,
      asOfDate: input.decisionDate,
      today_index: input.strategy.dayIndex,
      last_post_date: null,
      last_photo_date: null,
      last_video_date: null,
      last_voice_date: null,
      last_talking_date: null,
      last_rest_day: null,
      commercial_ratio: 0,
      trust_ratio: 0,
      interaction_ratio: 0.5,
      energy_level: 0.5,
      fatigue_score: 0,
      photo_count_this_week: 0,
      video_count_this_week: 0,
      talking_count_this_week: 0,
      voice_count_this_week: 0,
      rest_count_this_week: 0,
      selfie_count_this_week: 0,
      yesterday_had_selfie: false,
      yesterday_had_talking: false,
      consecutive_commercial_days: 0,
    } as import('./memoryTypes').CharacterMemory);

  const marketing = buildMarketingDecision({
    strategy: input.strategy,
    memory,
    operation,
    today_index: input.strategy.dayIndex,
  });

  return buildTodayDecisionFromMarketing(operation, marketing, input);
}

/** UI 用：素材类型中文标签 */
export const DECISION_MATERIAL_LABELS: Record<ExecutionJobType, string> = {
  photo: '角色照片',
  voice: '口播配音',
  talking_video: '口播影片',
  short_video: '无声短片',
};

export const DECISION_TONE_LABELS: Record<DecisionTone, string> = {
  life: '生活感',
  commercial: '商业转化',
  rest: '休息',
  outreach: '主动开发',
};
